import { Box, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { useState } from "react";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { ArrayHelper } from "@/common/helpers/array";
import { noop } from "@/common/helpers/function";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { useAuthorizationInfo } from "@/common/hooks/auth/useAuthorizationInfo";
import { VehicleDamageDtoAll } from "@/common/mapping/mappers/vehicleDamage";
import { ApiEnumName, enumService } from "@/common/services/enum";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  VehicleDamageDto,
  VehicleDamageGetPaginatedDto,
} from "@/core/api/generated/v1";

import AppBadge from "../../Badge/AppBadge";
import AppIconButton from "../../Button/AppIconButton";
import InlineApiEnumValue from "../../Enum/InlineApiEnumValue";
import DamageDetectionInline from "../DamageDetection/DamageDetectionInline";
import VehicleInline from "../Vehicle/VehicleInline";
import {
  VehicleDamageAutocompleteCustomFilters,
  VehicleDamageAutocompleteCustomFiltersValues,
  computeCustomFilters,
} from "./VehicleDamageAutocompleteCustomFiltersInput";
import { VehicleDamageAutocompleteCustomFiltersInputPopover } from "./VehicleDamageAutocompleteCustomFiltersInputPopover";
import VehicleDamageViewModal from "./View/VehicleDamageViewModal";

const defaultDisplayProps = {
  viewInModal: true,
  customFilters: true,
};

export interface VehicleDamageAutocompleteOption extends BaseAutocompleteOption<VehicleDamageDto> {
  subtitle?: string;
}

export const getVehicleDamageOptionTitle = (
  data: VehicleDamageDtoAll,
  isDetailed = false,
): string =>
  StringHelper.join(
    [
      (data.partType || data.partTypeLocal) && (data.damageType || data.damageTypeLocal)
        ? `${data.partType?.name || data.partTypeLocal?.name} - ${data.damageType?.name || data.damageTypeLocal?.name}`
        : data.localNumber,
      isDetailed ? enumService.getEnumValueName(ApiEnumName.VehicleArea, data.area) : undefined,
      isDetailed
        ? enumService.getEnumValueName(ApiEnumName.VehicleProjection, data.projection)
        : undefined,
    ],
    ", ",
    {
      skipEmpty: true,
      uniqueOnly: true,
    },
  );

export const vehicleDamageToOption = (data: VehicleDamageDto): VehicleDamageAutocompleteOption => ({
  id: data.id!,
  title: getVehicleDamageOptionTitle(data, true),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface VehicleDamageAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<VehicleDamageDto> {
  searchFilters: {
    vehicleId: string;
  } & Partial<Omit<VehicleDamageGetPaginatedDto, "search" | "includeIds">>;
  displayProps?: Partial<typeof defaultDisplayProps>;
  /** Custom filters can be changed by user. */
  defaultCustomFilters?: VehicleDamageAutocompleteCustomFiltersValues;
}

export default function VehicleDamageAutocomplete({
  searchFilters,
  displayProps,
  defaultCustomFilters,

  isPreload = true,
  ...otherProps
}: VehicleDamageAutocompleteProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const authorizationInfo = useAuthorizationInfo();

  const [customFilters, setCustomFilters] = useState<VehicleDamageAutocompleteCustomFilters | null>(
    () => (defaultCustomFilters ? computeCustomFilters(defaultCustomFilters) : null),
  );
  const [isCustomFiltersChanged, setIsCustomFiltersChanged] = useState(false);

  // set default filters (when changed) if local filters not set yet
  if (defaultCustomFilters && (!customFilters || !customFilters.isAny)) {
    const newCustomFilters = computeCustomFilters(defaultCustomFilters);
    if (newCustomFilters.isAny) {
      setCustomFilters(newCustomFilters);
    }
  }

  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={vehicleDamageToOption}
      isPreload={isPreload}
      isIgnoreCanLoadCondition={isCustomFiltersChanged} // hack to ensure refetch on custom filters change
      request={{
        requestFunc: apiClient.vehicleDamagesApi.apiV1VehiclesVehicleIdDamagesGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          vehicleId: searchFilters.vehicleId,
          vehicleDamageGetPaginatedDto: {
            ...searchFilters,

            // prefer predefined filters over custom user filters
            partTypeId: searchFilters?.partTypeId || customFilters?.values?.partTypeId,
            damageTypeId: searchFilters?.damageTypeId || customFilters?.values?.damageTypeId,
            area:
              searchFilters?.area || customFilters?.values?.area
                ? { equal: customFilters?.values?.area }
                : undefined,
            projection:
              searchFilters?.projection || customFilters?.values?.projection
                ? { equal: customFilters?.values?.projection }
                : undefined,
            stateV2:
              searchFilters?.stateV2 || customFilters?.values?.states
                ? { anyOf: customFilters?.values?.states }
                : undefined,
            correctionStatus:
              searchFilters?.correctionStatus || customFilters?.values?.correctionStatus
                ? { equal: customFilters?.values?.correctionStatus }
                : undefined,
            ids: ArrayHelper.undefinedIfEmpty([
              ...(searchFilters?.ids || []),
              ...(customFilters?.values?.suggestedIds || []),
              ...(customFilters?.values?.restrictedIds || []),
            ]),
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          vehicleDamageGetPaginatedDto: {
            ...params.vehicleDamageGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters, customFilters],
      }}
      label='Vehicle damage'
      placeholder='Search for vehicle damage...'
      renderOption={(props, option: VehicleDamageAutocompleteOption) => {
        const { key, ...rest } = props;

        return (
          <ListItem key={key} {...rest}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='vehicleDamage' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    component='div'
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                  >
                    {option.title}
                  </Typography>

                  {option.data && (
                    <Typography component='div' variant='body2'>
                      <Box>
                        {option.data.localNumber},{" "}
                        <InlineApiEnumValue
                          type={ApiEnumName.VehicleDamageState}
                          value={option.data.state}
                        />
                        {option.data.correctionStatus && (
                          <>
                            ,{" "}
                            <InlineApiEnumValue
                              type={ApiEnumName.DamageCorrectionStatus}
                              value={option.data.correctionStatus}
                            />
                          </>
                        )}
                        {/* <InlineApiEnumValue type='VehicleArea' value={option.data.area} />,{" "}
                        <InlineApiEnumValue
                          type='VehicleProjection'
                          value={option.data.projection}
                        /> */}
                      </Box>

                      <Box>
                        <VehicleInline
                          entity={option.data.vehicle}
                          withIcon={false}
                          withTooltip={false}
                        />
                      </Box>

                      {option.data?.damageDetection && (
                        <Box>
                          <DamageDetectionInline
                            entity={option.data?.damageDetection}
                            withIcon={false}
                            withTooltip={false}
                          />
                        </Box>
                      )}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      entityViewModal={{
        if:
          displayProps?.viewInModal &&
          authorizationInfo.hasPermissions([AppPermission.VehicleDamageRead]),
        renderModal: (params) => (
          <VehicleDamageViewModal
            {...params.dialogProps}
            viewProps={{
              vehicleId: undefined,
              vehicleDamageId: undefined,
              vehicleDamage: params.entity,
              onDelete: null,
            }}
          />
        ),
      }}
      endAdornmentActions={{
        actions: [
          {
            if: displayProps?.customFilters,
            renderAction: (params) => (
              <VehicleDamageAutocompleteCustomFiltersInputPopover
                clickBehavior={{}}
                trigger={
                  <AppIconButton {...params.iconButtonProps} color='default' title='Filters'>
                    <AppIcon of='filter' />
                    {customFilters?.isAny && (
                      <AppBadge
                        sx={{ position: "absolute", top: 0, right: 0 }}
                        color='secondary'
                        overlap='circular'
                        badgeContent={customFilters?.count}
                      />
                    )}
                  </AppIconButton>
                }
                subProps={{
                  searchFilters: searchFilters,
                  defaultFilters: customFilters?.values,
                  onSave: (newValue) => {
                    setIsCustomFiltersChanged(true);
                    setCustomFilters(newValue);
                  },
                  onCancel: noop,
                }}
              />
            ),
          },
        ],
      }}
      {...otherProps}
    />
  );
}
